import {connect} from 'pwa-helpers';
import {LitElement, html, css} from 'lit';
import {property} from 'lit/decorators/property.js';
import {when} from 'lit/directives/when.js';
import {repeat} from 'lit/directives/repeat.js';
import {customElement} from 'lit/decorators/custom-element.js';
import {msg} from '@lit/localize';
import {ConfigurationInterpreter} from '@viamedici-spc/configurator-ts';

import store from '../../store';
import {conditional} from '../../util/conditional';
import {getProductData} from '../../util/getProductData';
import {filterAndJoin} from '../../util/filterAndJoin';
import {pimText, pimTextDebug, spcExpliciteChoiceDecision} from '../../util/values';
import {
	selectDimensionHeight,
	selectDimensionWidth,
	selectDimensionDepth,
	selectDimensionDiameter,
	selectWeight,
	selectWindsurfacearea,
	selectSpcConfiguration,
	selectProductData, selectProductDatas, selectSpcConfigurations, selectSpcSessions, selectDimensionLength,
} from '../../selectors';


@customElement('shared-technical-data-attributes')
export class SharedTechnicalDataAttributes extends connect(store)(LitElement) {

	static styles = [
		css`
			:host {
				margin: 0;
				display: flex;
				flex-direction: column;
				container-type: inline-size;
			}
			.subheadline {
				font: var(--font-specsheet-print-small-text);
				font-size: 2cqw;
				margin-top: 5cqw;
				color: #939393;
			}
		`]
	;

	@property({type: Object})
	model = {};

	constructor() {
		super();
		this.productData = null;
		this.instanceId = 'default';
		store.subscribe(() => this.requestUpdate());
	}
	stateChanged(state) {
		this.state = state;
		this.status = state.status;
		this.debugShowAll = state.status.debugShowAll;
		this.lang = state.status.lang;
		this.lightProfile = state.lightProfile.data;
		this.conditionalVariantValues = state.productData.conditionalVariantValues;

		if (this.model.model_id && this.model.model_id !== '') {
			this.instanceId = 'modelPage_' + this.model.model_index;
			const productDatas = selectProductDatas(this.state);
			if (Object.hasOwn(productDatas, this.model?.model_id)) {
				this.productData = productDatas[this.model.model_id].productData;
				this.conditionalVariantValues = productDatas[this.model.model_id].conditionalVariantValues;
			}
			const spcConfigurations = selectSpcConfigurations(state);
			if (spcConfigurations && Object.hasOwn(spcConfigurations, this.instanceId)) {
				this.spcConfiguration = spcConfigurations[this.instanceId];
			}
		} else {
			this.productData = selectProductData(this.state);
			this.spcConfiguration = selectSpcConfiguration(state);
		}
	}

	getValueFromProductData(featureId, featureValueId) {
		const pd = getProductData(featureId, this.productData);
		return pd?.values?.find(val => val.valueId === featureValueId);
	}

	t(featureId, context, value, prefix, suffix) {
		return this.debug ?
			pimTextDebug(this.lang, featureId, context, this.spcConfiguration, this.state.productData.translations, value, prefix, suffix) :
			pimText(this.lang, featureId, context, this.spcConfiguration, this.state.productData.translations, value, prefix, suffix);
	}

	c(id) {
		return conditional(this.spcConfiguration, this.conditionalVariantValues, id);
	}

	render() {
		const s = this.state;

		const dimensionsLabel = this.t('TE_Dimensions', 'D');
		const dimensionDiameter = selectDimensionDiameter(s);
		const dimensionDepth = selectDimensionDepth(s);
		const dimensionLength = selectDimensionLength(s);
		const dimensionWidth = selectDimensionWidth(s);
		const dimensionHeight = selectDimensionHeight(s);
		const isDimensionsPresent = dimensionsLabel || dimensionDepth || dimensionWidth || dimensionHeight;

		const applicableStandards = filterAndJoin([
			this.t(this.c('DescriptiveAttributeReference.LU_IK'), 'DV.S'),
			this.t(this.c('DescriptiveAttributeReference.LU_IP'), 'DV.S'),
			this.t('root::Heads::Head::EBlock::EB_ElectricalProtectionClass', 'FV.S'),
			this.t(this.c('DescriptiveAttributeReference.LU_ENEC_CertificateDescription'), 'DV.S'),
			this.t(this.c('DescriptiveAttributeReference.LU_CE'), 'DV.S')
		], ', ');

		return html`
			<!-- CRI -->
			<shared-specsheet-feature condition="${this.t(this.c('DescriptiveAttributeReference.LE_CRI'), 'DV')}">
				<trans-label slot="label" key="LE_CRI" context="D"></trans-label>
				<span slot="value" class="value">
					${filterAndJoin([
						this.t('LE_CRI', 'D', null, '', ' ') + this.t(this.c('DescriptiveAttributeReference.LE_CRI'), 'DV')
					], ', ')}
				</span>
			</shared-specsheet-feature>

			<!--  -->
			<shared-specsheet-feature condition="${this.t('HS_LightEmission', 'FV.S', null, '', '')}">
				<trans-label slot="label" key="HS_LightEmission" context="F"></trans-label>
				<span slot="value">
					${filterAndJoin([
						this.t('root::LightEngine_1::LE_UpLight', 'FV.S'),
						this.t('shared::Context::HS_LightEmissionCAFilter', 'FV.S')
					], ', ')}
				</span>
			</shared-specsheet-feature>

			<shared-specsheet-feature condition="${this.c('REDTAB.RETURN_LU_ULOR')}">
				<trans-label slot="label" key="LU_ULOR" context="D"></trans-label>
				<span slot="value">${this.c('REDTAB.RETURN_LU_ULOR')} %</span>
			</shared-specsheet-feature>

			<shared-specsheet-feature condition="${this.c('REDTAB.RETURN_LU_GD_Class')}">
				<trans-label slot="label" key="LU_GD_Class" context="D"></trans-label>
				<span slot="value">${this.c('REDTAB.RETURN_LU_GD_Class')}</span>
			</shared-specsheet-feature>

			<shared-specsheet-feature condition="${this.c('REDTAB.RETURN_LU_CIE_Code')}">
				<trans-label slot="label" key="LU_CIE_Code" context="D"></trans-label>
				<span slot="value">${this.c('REDTAB.RETURN_LU_CIE_Code')}</span>
			</shared-specsheet-feature>

			<shared-specsheet-feature condition="${this.c('REDTAB.RETURN_LU_BUG_Rating')}">
				<trans-label slot="label" key="LU_BUG_Rating" context="D"></trans-label>
				<span slot="value">${this.c('REDTAB.RETURN_LU_BUG_Rating')}</span>
			</shared-specsheet-feature>

			<!-- Material -->
			${when(this.t('HS_HousingDescription', 'D.L') !== '', () => html`
				<shared-specsheet-feature>
					<trans-label slot="label" key="LU_LuminaireMaterial" context="D"></trans-label>
					<span slot="value">
						${filterAndJoin([
							this.t('HS_HousingDescription', 'D.L'),
							this.t(this.c('DescriptiveAttributeReference.LU_LuminaireMaterial'), 'DV.L'),
							this.t(this.c('DescriptiveAttributeReference.LE_LightEngineCover'), 'DV.L'),
							this.t(this.c('DescriptiveAttributeReference.LE_ReflectorMaterial'), 'DV.L'),
						], ', ')}
					</span>
				</shared-specsheet-feature>
			`)}

			<shared-specsheet-feature condition="${this.t(this.c('DescriptiveAttributeReference.HS_Attachement'), 'DV.L')}">
				<trans-label slot="label" key="HS_Attachement" context="D"></trans-label>
				<span slot="value">
					${this.t(this.c('DescriptiveAttributeReference.HS_Attachement'), 'DV.L')}
				</span>
			</shared-specsheet-feature>

			<!-- Technische Region -->
			<shared-specsheet-feature>
				<trans-label slot="label" key="LU_TechnicalRegion" context="D"></trans-label>
				<trans-label slot="value" class="value" key="LU_TechnicalRegion.220-240V 50/60 Hz" context="DV"></trans-label>
			</shared-specsheet-feature>

			<!-- Umgebungstemperatur -->
			<shared-specsheet-feature condition="${this.t(this.c('DescriptiveAttributeReference.LU_AmbientTemperature'), 'DV.S')}">
				<trans-label slot="label" key="LU_AmbientTemperature" context="D"></trans-label>
				<span
					slot="value">${this.c('DescriptiveAttributeReference.LU_AmbientTemperature')} ${this.t(this.c('DescriptiveAttributeReference.LU_AmbientTemperature'), 'DV.S')}</span>
			</shared-specsheet-feature>

			<!-- Betriebsgerät -->
			<shared-specsheet-feature>
				<trans-label slot="label" key="EB_EBlockOperatingGearDescription" context="D"></trans-label>
				<trans-label slot="value" class="value" key="EB_EBlockOperatingGearDescription.ElectronicConverter" context="DV.S"></trans-label>
			</shared-specsheet-feature>

			<!-- Überspannungsschutz -->
			<shared-specsheet-feature>
				<trans-label slot="label" key="LU_SurgeProtection" context="D"></trans-label>
				<trans-label slot="value" key="LU_SurgeProtection.10kV" context="DV.S"></trans-label>
			</shared-specsheet-feature>

			<!-- Anschlussleitung -->
			<shared-specsheet-feature condition="${this.t('root::Heads::Head::Housing::HS_HousingConnection', 'FV.S')}">
				<trans-label slot="label" key="root::Heads::Head::Housing::HS_HousingConnection" context="F"></trans-label>
				<span slot="value" class="value">
					${filterAndJoin([
						this.t('root::Heads::Head::Housing::HS_HousingConnection', 'FV.S'),
						this.t(this.c('DescriptiveAttributeReference.LU_CableType'), 'DV')
					], ', ')}
				</span>
			</shared-specsheet-feature>

			${when(this.instanceId === 'default', () => html`
				<shared-specsheet-feature condition="${isDimensionsPresent}">
					<trans-label slot="label" key="TE_Dimensions" context="D"></trans-label>
					<span slot="value" class="value">
								${dimensionDiameter ? html`
									<trans-label slot="label" class="value" key="HS_HousingDiameter" context="D" suffix=":"></trans-label>
									${dimensionDiameter}
									<trans-label slot="label" class="value" key="HS_HousingDiameterUnit.mm" context="DV"></trans-label>,
								` : ''}

								${dimensionDepth ? html`
									<trans-label slot="label" class="value" key="HS_HousingLength" context="D" suffix=":"></trans-label>
									${dimensionDepth}
									<trans-label slot="label" class="value" key="HS_HousingLengthUnit.mm" context="DV"></trans-label>,
								` : ''}

								${dimensionLength ? html`
									<trans-label slot="label" class="value" key="HS_HousingLength" context="D" suffix=":"></trans-label>
									${dimensionLength}
									<trans-label slot="label" class="value" key="HS_HousingLengthUnit.mm" context="DV"></trans-label>,
								` : ''}

								${dimensionWidth ? html`
									<trans-label slot="label" class="value" key="HS_HousingWidth" context="D" suffix=":"></trans-label>
									${dimensionWidth}
									<trans-label slot="label" class="value" key="HS_HousingWidthUnit.mm" context="DV"></trans-label>,
								` : ''}

								${dimensionHeight ? html`
									<trans-label slot="label" class="value" key="HS_HousingHeight" context="D" suffix=":"></trans-label>
									${dimensionHeight}
									<trans-label slot="label" class="value" key="HS_HousingHeightUnit.mm" context="DV"></trans-label>
								` : ''}
							</span>
				</shared-specsheet-feature>

				<!-- Gewicht -->
				<shared-specsheet-feature>
					<trans-label slot="label" key="LU_LuminaireWeight" context="D"></trans-label>
					<span slot="value">${selectWeight(s) + this.t('LU_LuminaireWeightUnit', 'D', null, ' ')}</span>
				</shared-specsheet-feature>

				<!-- Wind -->
				<shared-specsheet-feature>
					<trans-label slot="label" key="HS_WindSurfaceArea" context="D"></trans-label>
					<span slot="value">${selectWindsurfacearea(s) + this.t('HS_WindSurfaceAreaUnit.M2', 'DV.S', null, ' ')}</span>
				</shared-specsheet-feature>
			`)}

			${when(applicableStandards.trim() !== '', () => html`
				<shared-specsheet-feature>
					<trans-label slot="label" key="LU_ApplicableStandards" context="D"></trans-label>
					<span slot="value" class="value">
						${applicableStandards}
					</span>
				</shared-specsheet-feature>
			`)}

			<!-- Wartung -->
			${when(this.t('LU_Maintanance', 'D.S') !== '', () => html`
				<shared-specsheet-feature>
					<trans-label slot="label" key="LU_Maintanance" context="D"></trans-label>
					<trans-label slot="value" key="LU_Maintanance" context="D.S"></trans-label>
				</shared-specsheet-feature>
			`)}

			<!-- Garantie -->
			${when(this.t('LU_Warranty', 'D.L') !== '', () => html`
				<shared-specsheet-feature>
					<trans-label slot="label" key="LU_Warranty" context="D"></trans-label>
					<trans-label slot="value" key="LU_Warranty" context="D.L"></trans-label>
				</shared-specsheet-feature>
			`)}


		`;
	}
}
